import { useEffect, useState, useCallback } from "react";

const MOBILE_BREAKPOINT_PX = 765;
const MOBILE_MARKER_HEIGHT = 417;

/**
 * Manages scroll/intersection state for the quote query component: sticky header, marker height, mobile expand.
 * @param {React.RefObject} quoteQueryRef
 * @param {boolean} inModal
 * @returns {{ isScrolled: boolean, mobileIsActive: boolean, quoteQueryMarkerHeight: number, toggleForMobile: function, checkOverflow: function }}
 */
const useQuoteQueryScroll = (quoteQueryRef, inModal) => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [mobileIsActive, setMobileIsActive] = useState(false);
    const [quoteQueryMarkerHeight, setQuoteQueryMarkerHeight] = useState(0);

    const checkOverflow = useCallback(() => {
        const root = document.documentElement;
        const shouldLock =
            isScrolled && mobileIsActive && !window.matchMedia(`(min-width: ${MOBILE_BREAKPOINT_PX}px)`).matches;
        root.style.overflow = shouldLock ? "hidden" : "";
        return shouldLock;
    }, [isScrolled, mobileIsActive]);

    const toggleForMobile = useCallback(() => {
        setMobileIsActive((prev) => !prev);
    }, []);

    useEffect(() => {
        const resizeHandler = () => checkOverflow();
        resizeHandler();
        window.addEventListener("resize", resizeHandler);
        return () => window.removeEventListener("resize", resizeHandler);
    }, [checkOverflow]);

    useEffect(() => {
        if (inModal || !quoteQueryRef.current) return;

        const header = document.querySelector(".header");

        const observerCallback = (entries) => {
            entries.forEach((entry) => {
                const isTarget =
                    entry.target.classList.contains("home__query__content") ||
                    (entry.target.classList.contains("wrap") && entry.target.querySelector(".quote-query-marker"));

                if (!isTarget) return;

                const isCompletelyOutOfView = !entry.isIntersecting;

                if (isCompletelyOutOfView) {
                    if (entry.target.classList.contains("wrap")) {
                        entry.target.classList.add("quote-scrolled");
                    }
                    const currentScrollY = window.scrollY;
                    setIsScrolled(true);
                    if (header) header.classList.add("header--quote-query-scrolled");

                    if (window.matchMedia(`(min-width: ${MOBILE_BREAKPOINT_PX}px)`).matches) {
                        setQuoteQueryMarkerHeight(quoteQueryRef.current?.clientHeight ?? 0);
                    } else {
                        setQuoteQueryMarkerHeight(MOBILE_MARKER_HEIGHT);
                    }
                    requestAnimationFrame(() => window.scrollTo(0, currentScrollY));
                } else {
                    setIsScrolled(false);
                    setMobileIsActive(false);
                    if (header) header.classList.remove("header--quote-query-scrolled");
                    if (entry.target.classList?.remove) {
                        entry.target.classList.remove("quote-scrolled");
                    }
                    setQuoteQueryMarkerHeight(0);
                }
            });
        };

        const observer = new IntersectionObserver(observerCallback, {
            threshold: 0,
            rootMargin: "0px",
        });

        const homeQueryContentElement = document.querySelector(".home__query__content");
        const wrapWithMarker = document.querySelector(".wrap:has(.quote-query-marker)");
        const element = homeQueryContentElement ?? wrapWithMarker;
        if (element) observer.observe(element);

        return () => observer.disconnect();
    }, [inModal, quoteQueryRef]);

    return {
        isScrolled,
        mobileIsActive,
        quoteQueryMarkerHeight,
        toggleForMobile,
        checkOverflow,
    };
};

export default useQuoteQueryScroll;
